<template>
  <v-navigation-drawer
    :model-value="onboarding.helpDrawerOpen"
    location="end"
    temporary
    width="360"
    class="help-drawer"
    :dir="chromeHebrew ? 'rtl' : 'ltr'"
    :lang="chromeHebrew ? 'he' : 'en'"
    @update:model-value="onDrawerToggle"
  >
    <div class="help-drawer__header pa-4 d-flex align-center">
      <v-icon class="me-2" color="primary">mdi-help-circle-outline</v-icon>
      <span class="text-h6 flex-grow-1">{{ chrome.title }}</span>
      <v-btn
        icon
        variant="text"
        size="small"
        data-cy="help-drawer-close"
        :aria-label="chrome.close"
        @click="close"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </div>

    <v-tabs v-model="tab" density="compact" color="primary" class="px-2">
      <v-tab v-if="!isPracticePage" value="collaborate">
        {{ chrome.collaborate }}
      </v-tab>
      <v-tab value="tools">{{ chrome.tools }}</v-tab>
      <v-tab value="page">{{ chrome.page }}</v-tab>
    </v-tabs>

    <v-tabs-window v-model="tab" class="help-drawer__body pa-4">
      <v-tabs-window-item value="collaborate">
        <h3 class="text-subtitle-1 font-weight-bold mb-2">
          {{ collabTitle }}
        </h3>
        <v-list density="compact" class="pa-0">
          <v-list-item
            v-for="(step, i) in collabSteps"
            :key="i"
            class="px-0"
          >
            <template #prepend>
              <v-avatar size="24" color="primary" class="me-2">
                <span class="text-caption">{{ i + 1 }}</span>
              </v-avatar>
            </template>
            <v-list-item-title class="text-body-2">{{ step }}</v-list-item-title>
          </v-list-item>
        </v-list>
        <TutorialVideoList
          class="mt-4"
          :videos="classroomTutorials"
          :heading="chrome.watch"
          :hebrew="chromeHebrew"
        />
      </v-tabs-window-item>

      <v-tabs-window-item value="tools">
        <h3 class="text-subtitle-1 font-weight-bold mb-1">
          {{ tools.title }}
        </h3>
        <p class="text-body-2 text-medium-emphasis mb-3">
          {{ tools.intro }}
        </p>
        <div class="mb-4">
          <div class="text-caption font-weight-bold text-uppercase mb-1">
            {{ keyboard.title }}
          </div>
          <p class="text-body-2 mb-2">{{ keyboard.intro }}</p>
          <ul class="help-drawer__list text-body-2 ps-4 mb-0">
            <li v-for="tip in keyboard.tips" :key="tip">{{ tip }}</li>
          </ul>
        </div>
        <div class="mb-4">
          <div class="text-caption font-weight-bold text-uppercase mb-1">
            {{ techniques.title }}
          </div>
          <ul class="help-drawer__list text-body-2 ps-4 mb-0">
            <li
              v-for="item in techniques.items"
              :key="item"
            >
              {{ item }}
            </li>
          </ul>
        </div>
        <div v-for="group in tools.groups" :key="group.name" class="mb-3">
          <div class="text-caption font-weight-bold text-uppercase mb-1">
            {{ group.name }}
          </div>
          <ul class="help-drawer__list text-body-2 ps-4">
            <li v-for="tool in group.tools" :key="tool">{{ tool }}</li>
          </ul>
        </div>
        <p class="text-caption text-medium-emphasis mb-4">
          {{ basics.exitHint }}
        </p>
        <TutorialVideoList
          :videos="toolTutorials"
          :heading="chrome.watch"
          :hebrew="chromeHebrew"
        />
      </v-tabs-window-item>

      <v-tabs-window-item value="page">
        <h3 class="text-subtitle-1 font-weight-bold mb-2">
          {{ pageHelp.title }}
        </h3>
        <p
          v-for="(line, i) in pageHelp.lines"
          :key="i"
          class="text-body-2 mb-2"
        >
          {{ line }}
        </p>
        <TutorialVideoList
          class="mt-4"
          :videos="pageTutorials"
          :heading="chrome.watch"
          :hebrew="chromeHebrew"
        />
      </v-tabs-window-item>
    </v-tabs-window>
  </v-navigation-drawer>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useOnboardingStore } from "../store/pinia/onboardingStore";
import { useUserStore } from "../store/pinia/userStore";
import { useBoardContextStore } from "../store/pinia/boardContextStore";
import {
  boardContextToHelpKey,
  getPageHelp,
  BOARD_ROLE_BANNERS,
  LIST_INTROS,
} from "../constants/helpCopy";
import { usePracticeLocale } from "../composables/usePracticeLocale";
import {
  collabHelp,
  editingBasicsHelp,
  editingTechniquesHelp,
  helpChrome,
  keyboardHelp,
  practicePageHelp,
  toolsHelp,
} from "../i18n/practiceChrome";
import TutorialVideoList from "./TutorialVideoList.vue";
import { tutorialsFor } from "../config/tutorials";

const toolTutorials = tutorialsFor("tools");
const practiceTutorials = tutorialsFor("practice");
const classroomTutorials = tutorialsFor("classroom");

const onboarding = useOnboardingStore();
const userStore = useUserStore();
const boardContext = useBoardContextStore();
const tab = ref("collaborate");
const { chromeHebrew } = usePracticeLocale();
const chrome = computed(() => helpChrome(chromeHebrew.value));
const tools = computed(() => toolsHelp(chromeHebrew.value));
const keyboard = computed(() => keyboardHelp(chromeHebrew.value));
const techniques = computed(() => editingTechniquesHelp(chromeHebrew.value));
const basics = computed(() => editingBasicsHelp(chromeHebrew.value));

const collab = computed(() =>
  collabHelp(userStore.isTeacher(), chromeHebrew.value),
);
const collabTitle = computed(() => collab.value.title);
const collabSteps = computed(() => collab.value.steps);

const isPracticePage = computed(
  () =>
    boardContext.level === "practice" ||
    boardContext.level === "practice-list",
);

/** Symbol and toolbar clips on every page, not only the welcome screen. */
const pageTutorials = computed(() =>
  isPracticePage.value
    ? [...practiceTutorials, ...toolTutorials]
    : toolTutorials,
);

// Practice has no lesson collaboration; open on the page tab there.
watch(
  () => onboarding.helpDrawerOpen,
  (open) => {
    if (open && isPracticePage.value && tab.value === "collaborate") {
      tab.value = "page";
    }
  },
);

const pageHelp = computed(() => {
  const practice = practicePageHelp(boardContext.level, chromeHebrew.value);
  if (practice) return practice;
  const key = boardContextToHelpKey(boardContext.level);
  const base = getPageHelp(key);
  if (!userStore.isTeacher() && key === "question") {
    return {
      title: base.title,
      lines: [BOARD_ROLE_BANNERS.questionStudent, LIST_INTROS.questions],
    };
  }
  if (!userStore.isTeacher() && key === "answer") {
    return {
      title: base.title,
      lines: [BOARD_ROLE_BANNERS.answerStudent],
    };
  }
  return base;
});

function close() {
  onboarding.closeHelpDrawer();
}

function onDrawerToggle(open: boolean) {
  if (!open) {
    close();
  }
}
</script>

<style scoped>
.help-drawer__header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.help-drawer__body {
  overflow-y: auto;
  max-height: calc(100vh - 130px);
}

.help-drawer__list {
  margin: 0;
}
</style>
